import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { waitForElement, suppressAzPopup, clearAzPopup } from "./guideUtils";
import GuidePopup from "./GuidePopup";

// Drives the "Find an ungated product" onboarding guide (step 2 of the /home
// roadmap), triggered by arriving on /retail?guide=gated.
//
// Flow:
//   1. Spotlight the "Gated" filter button (#gatedFilterBtn).
//   2. User clicks it -> the gated-filter modal opens and spotlight #1 ends.
//   3. User closes that modal -> FilterBar fires a "nepeto:gatedFilterClosed"
//      event -> we spotlight the "Next page" button (#nextPageBtn) and explain
//      the 200-product eligibility scan.
//
// Desktop uses react-joyride (disabled on mobile), so mobile gets the same two
// messages as plain "Got it" popups instead.
function GatedGuideTrigger({ isMobile, isAuthenticated, isLoading, setJoyrideRun }) {
  const location = useLocation();
  // 0 = off, 1 = first message (gated filter), 2 = second message (next page)
  const [mobileStep, setMobileStep] = useState(0);
  // Desktop: start-timestamp while the guide is mid-flight (between spotlight
  // #1 and the modal-close that triggers spotlight #2); null = inactive. The
  // timestamp lets us ignore a long-stale flight (user skipped the tour and
  // opens the gated modal organically much later).
  const guideActiveRef = useRef(null);
  const GUIDE_TTL_MS = 10 * 60 * 1000;
  // The "wait for #nextPageBtn" poll — tracked so navigation can cancel it.
  const nextPollRef = useRef(null);
  const clearNextPoll = () => {
    if (nextPollRef.current) {
      nextPollRef.current(); // cancel the waitForElement poll
      nextPollRef.current = null;
    }
  };
  // Guards the start effect against re-firing while still on /retail (e.g. an
  // isMobile flip on resize) — stays true for the whole /retail visit.
  const startedRef = useRef(false);

  // Leaving /retail ends the guide and re-arms it; also stop suppressing the
  // Amazon-sync popup and cancel any pending "Next page" poll (its button id
  // also exists on /distributors and /wholesale — never fire there).
  useEffect(() => {
    if (location.pathname !== "/retail") {
      guideActiveRef.current = null;
      startedRef.current = false;
      clearNextPoll();
      setMobileStep(0);
      clearAzPopup();
      // Disarm the App-level Joyride too, or its spotlight lingers as a dead
      // overlay / re-appears on /distributors and /wholesale (same button ids). (F1)
      setJoyrideRun("");
    }
  }, [location.pathname, setJoyrideRun]);

  // Cancel the poll if the component ever unmounts.
  useEffect(() => () => clearNextPoll(), []);

  // --- Kick off the guide when we land on /retail?guide=gated ---
  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get("guide") !== "gated") return;
    if (location.pathname !== "/retail") return;
    if (isLoading || !isAuthenticated) return;

    startedRef.current = true;
    // Keep the auto Amazon-sync popup from covering the spotlight until finished.
    suppressAzPopup();
    // Strip the param from the URL bar only (does NOT touch the router location,
    // so it won't disturb the page / joyride mid-spotlight).
    const cleanUrl = () => window.history.replaceState({}, "", "/retail");

    if (isMobile) {
      setMobileStep(1);
      cleanUrl();
      return;
    }

    // Desktop: poll until the Gated button mounts (table needs to load), then
    // spotlight it.
    guideActiveRef.current = Date.now();
    let btn = null;
    let onBtnClick = null;
    const cancel = waitForElement("#gatedFilterBtn", {
      tries: 40,
      interval: 500,
      onFound: (el) => {
        btn = el;
        setJoyrideRun("");
        setJoyrideRun("gatedFilter");
        cleanUrl();
        // Clicking the lit button opens the filter AND ends spotlight #1.
        onBtnClick = () => setJoyrideRun("");
        btn.addEventListener("click", onBtnClick, { once: true });
      },
      onTimeout: () => {
        guideActiveRef.current = null;
        clearAzPopup();
      },
    });

    return () => {
      cancel();
      if (btn && onBtnClick) btn.removeEventListener("click", onBtnClick);
    };
  }, [location, isMobile, isAuthenticated, isLoading, setJoyrideRun]);

  // --- Desktop: when the gated filter modal closes, spotlight "Next page" ---
  useEffect(() => {
    if (isMobile) return;
    const onClosed = () => {
      if (!guideActiveRef.current) return;
      if (window.location.pathname !== "/retail") return;
      if (Date.now() - guideActiveRef.current > GUIDE_TTL_MS) {
        // Stale flight (tour skipped long ago) — don't replay out of context.
        guideActiveRef.current = null;
        clearAzPopup();
        return;
      }
      guideActiveRef.current = null;
      // Wait for the "Next page" button. Enabling the ungated filter unmounts
      // the table for the whole eligibility re-check (~200 ASINs), which can
      // take a while — poll generously (100 x 300ms = 30s).
      clearNextPoll();
      nextPollRef.current = waitForElement("#nextPageBtn", {
        tries: 100,
        interval: 300,
        requireVisible: false,
        onFound: (el) => {
          clearNextPoll();
          setJoyrideRun("");
          setJoyrideRun("gatedNextPage");
          // Guide finished its job — let the Amazon-sync popup behave normally.
          clearAzPopup();
          // Clicking "Next page" ends this spotlight, same as clicking the Gated
          // button ends the first one. Without this the "How the scan works" popup
          // stays up after the user pages (spotlightClicks lets the click through,
          // but nothing was clearing joyrideRun).
          el.addEventListener("click", () => setJoyrideRun(""), { once: true });
        },
        onTimeout: () => {
          clearNextPoll();
          clearAzPopup();
        },
      });
    };
    window.addEventListener("nepeto:gatedFilterClosed", onClosed);
    return () => window.removeEventListener("nepeto:gatedFilterClosed", onClosed);
  }, [isMobile, setJoyrideRun]);

  if (mobileStep === 0) return null;

  // --- Mobile popups (two steps) ---
  const tip =
    mobileStep === 1
      ? (
        <>
          We've pre-selected a few categories new sellers are more likely to be
          ungated in. To find one, tap the{" "}
          <strong style={{ color: "#8c3e7c" }}>Gated</strong> button and choose{" "}
          <strong style={{ color: "#8c3e7c" }}>"Show Only Ungated ASINs"</strong>.
        </>
      )
      : (
        <>
          When you use these filters, Nepeto takes <strong>200 products</strong> and
          checks your selling eligibility for each one. If only a few show up, keep{" "}
          <strong style={{ color: "#8c3e7c" }}>swiping through the products</strong> to
          scan 200 more!
        </>
      );

  return (
    <GuidePopup onDismiss={() => setMobileStep((s) => (s === 1 ? 2 : 0))}>
      {tip}
    </GuidePopup>
  );
}

export default GatedGuideTrigger;
